/**
 * Balance view for /billing/school — the PREPAID (balance load-up)
 * variant of the school Billing tab. The parent-facing wallet page's
 * layout, fed by the school account endpoints:
 *
 *   1. Balance card — the ORGANIZATION account's balance + "Top up" CTA
 *      (no auto-reload: that's a family-wallet feature; org accounts
 *      load up manually or by wire).
 *   2. Billing history — the reused `LedgerTable` over
 *      `GET /api/billing-school/account/ledger`.
 *
 * Top-ups reuse `TopUpSheet` against the school-scoped endpoints — the
 * exact wiring the checkout's insufficient-funds gate uses
 * (`pages/school-checkout.tsx`), including the org-card badge, the
 * load-up incentive callout, and the webhook-lag balance watcher.
 */

import { DEFAULT_MIN_TOP_UP_CENTS } from '@ordo/constants/wallet'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader } from '@ordo/ui/card'
import { Skeleton } from '@ordo/ui/skeleton'
import { toast } from '@ordo/ui/sonner'
import { Loader2, Plus } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import {
  createSchoolTestTopUp,
  createSchoolTopUpIntent,
  schoolBillingAccountQueryKey,
  useSchoolBillingAccountQuery,
  useSchoolBillingLedgerQuery,
} from '@/api/billing-school-account'
import { formatCents } from '@/components/billing/helpers'
import { LedgerTable } from '@/components/billing/ledger-table'
import { SchoolPaymentMethodsCard } from '@/components/billing/school-payment-methods-card'
import { formatBpsPercent, TopUpSheet, type TopUpSheetAccount } from '@/components/billing/top-up-sheet'
import { useSchoolTopUpWatch } from '@/components/billing/use-school-top-up-watch'
import { effectiveTopUpIncentiveBps } from '@/lib/school-orders/funds'

export interface SchoolBalanceSectionProps {
  /** The campus whose billing account is shown (the account itself is org-level). */
  schoolLocationId: string
  /** Header label — the campus or organization name. */
  schoolLabel: string | null
}

export function SchoolBalanceSection({ schoolLocationId, schoolLabel }: SchoolBalanceSectionProps) {
  const {
    data: accountData,
    isPending: accountPending,
    error: accountError,
    refetch: refetchAccount,
  } = useSchoolBillingAccountQuery(schoolLocationId)
  const {
    data: ledgerData,
    isPending: ledgerPending,
    refetch: refetchLedger,
  } = useSchoolBillingLedgerQuery(schoolLocationId)
  const account = accountData ?? null

  const [topUpOpen, setTopUpOpen] = useState(false)
  const topUpWatch = useSchoolTopUpWatch(schoolLocationId)

  useEffect(() => {
    if (accountError) {
      toast.error("Couldn't load the school balance", {
        description: accountError.message,
        action: {
          label: 'Retry',
          onClick: () => {
            void refetchAccount()
          },
        },
      })
    }
  }, [accountError, refetchAccount])

  // The history lags a Stripe top-up the same way the balance does (both
  // land on the webhook) — refresh it once the balance watcher settles.
  const watchWasPending = useRef(false)
  useEffect(() => {
    if (watchWasPending.current && !topUpWatch.pending) {
      void refetchLedger()
    }
    watchWasPending.current = topUpWatch.pending
  }, [topUpWatch.pending, refetchLedger])

  // Same org-account adapter the checkout's sheet uses — saved cards render
  // with the "Organization payment method" badge, never as a stranger card.
  const sheetAccount = useMemo<TopUpSheetAccount>(
    () => ({
      id: account?.accountId ?? schoolLocationId,
      currency: account?.currency ?? 'USD',
      // A school setting a funding floor on its own wallet is self-referential,
      // so ORGANIZATION accounts stay on the platform default — PROD-1317
      // scopes the configurable minimum to FAMILY wallets.
      minTopUpCents: DEFAULT_MIN_TOP_UP_CENTS,
      paymentMethods: account?.paymentMethods ?? [],
      defaultPaymentMethodRef: account?.defaultPaymentMethodRef ?? null,
    }),
    [account, schoolLocationId],
  )

  const incentiveBps = account ? effectiveTopUpIncentiveBps(account) : 0

  function handleTopUpOpenChange(open: boolean): void {
    setTopUpOpen(open)
    if (!open) {
      // Local test top-ups credit synchronously without firing onPaid —
      // refetch on close so the balance + history reflect them immediately.
      void refetchAccount()
      void refetchLedger()
    }
  }

  if (accountPending || (!account && !accountError)) {
    return <SchoolBalanceSectionSkeleton />
  }

  if (!account) {
    return (
      <Card className="p-8">
        <h2 className="text-lg font-medium">Balance unavailable</h2>
        <p className="mt-2 max-w-xl text-sm text-muted-foreground">
          We couldn't load the school's billing account. Try again, or contact Ordo if this keeps happening.
        </p>
        <Button
          className="mt-4 w-fit"
          variant="outline"
          onClick={() => {
            void refetchAccount()
          }}>
          Retry
        </Button>
      </Card>
    )
  }

  const negative = account.balanceCents < 0

  return (
    <div className="flex flex-col gap-6">
      {/* Balance card — the family BalanceCard's layout, minus auto-reload. */}
      <Card>
        <CardHeader className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1.5">
            <p className="text-sm text-muted-foreground">{schoolLabel ? `${schoolLabel} balance` : 'School balance'}</p>
            <h2 className={`text-4xl font-semibold tracking-tight ${negative ? 'text-destructive' : ''}`}>
              {formatCents(account.balanceCents, account.currency)}
            </h2>
            {negative && (
              <p className="text-xs text-muted-foreground">
                The balance is negative after a refund or dispute. Add funds to keep ordering.
              </p>
            )}
            {topUpWatch.pending || topUpWatch.stillProcessing ? (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground" role="status" aria-live="polite">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                {topUpWatch.stillProcessing
                  ? 'Still adding the funds — this can take a minute.'
                  : 'Updating the balance…'}
              </p>
            ) : null}
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            <Button variant="default" onClick={() => setTopUpOpen(true)}>
              <Plus className="mr-1.5 h-4 w-4" aria-hidden />
              Add funds
            </Button>
          </div>
        </CardHeader>
        <CardContent className="border-t [&:not(:first-child)]:pt-6">
          <p className="text-xs text-muted-foreground">
            School orders are debited from this balance at checkout.
            {incentiveBps > 0
              ? ` Added funds earn a ${formatBpsPercent(incentiveBps)} bonus credit when the payment settles.`
              : ''}
          </p>
        </CardContent>
      </Card>

      {/* Payment methods — org-wide saved cards (PROD-127). Sits directly
          under the balance because that is what they fund; the family wallet
          orders its cards the same way. */}
      <SchoolPaymentMethodsCard schoolLocationId={schoolLocationId} paymentMethods={account.paymentMethods} />

      {/* Billing history — the family ledger table over the org account. */}
      {ledgerPending ? (
        <LedgerTableSkeleton />
      ) : (
        <LedgerTable
          account={{
            currency: ledgerData?.currency ?? account.currency,
            ledger: { edges: (ledgerData?.entries ?? []).map((node) => ({ node })) },
          }}
          accountNoun="account"
        />
      )}

      <TopUpSheet
        open={topUpOpen}
        onOpenChange={handleTopUpOpenChange}
        account={sheetAccount}
        api={{
          createTopUpIntent: (vars) => createSchoolTopUpIntent({ ...vars, schoolLocationId }),
          createTestTopUp: (vars) => createSchoolTestTopUp({ ...vars, schoolLocationId }),
          testTopUpInvalidateKey: schoolBillingAccountQueryKey(schoolLocationId),
        }}
        paymentMethodBadge="Organization payment method"
        incentiveBps={incentiveBps}
        onPaid={({ amountCents }) => topUpWatch.watch({ amountCents })}
      />
    </div>
  )
}

/** Block-for-block placeholder for the balance card + history table. */
function SchoolBalanceSectionSkeleton() {
  return (
    <div className="flex flex-col gap-6" aria-busy="true" aria-label="Loading school balance">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-10 w-48" />
          </div>
          <Skeleton className="h-10 w-28" />
        </CardHeader>
        <CardContent className="border-t [&:not(:first-child)]:pt-6">
          <Skeleton className="h-3 w-80" />
        </CardContent>
      </Card>
      <PaymentMethodsCardSkeleton />
      <LedgerTableSkeleton />
    </div>
  )
}

function PaymentMethodsCardSkeleton() {
  return (
    <Card>
      <CardHeader className="flex flex-col gap-2">
        <Skeleton className="h-5 w-36" />
        <Skeleton className="h-3 w-64" />
      </CardHeader>
      <CardContent className="flex flex-col gap-1.5">
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-8 w-full" />
      </CardContent>
    </Card>
  )
}

function LedgerTableSkeleton() {
  return (
    <Card>
      <CardHeader className="flex flex-col gap-4">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-2">
            <Skeleton className="h-5 w-36" />
            <Skeleton className="h-3 w-44" />
          </div>
          <Skeleton className="h-9 w-36" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-7 w-20 rounded-full" />
          <Skeleton className="h-7 w-20 rounded-full" />
          <Skeleton className="h-7 w-20 rounded-full" />
        </div>
      </CardHeader>
      <CardContent className="grid gap-3">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </CardContent>
    </Card>
  )
}
